import { ArrowLeft, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import type { CardHole } from '../../lib/courses.ts';
import { DEFAULT_TEES, getCourseCard, saveCourseCard } from '../../lib/courses.ts';

/* Le carnet de parcours : pars, index de difficulté, distances par départ.
 *
 * ── Les départs en ONGLETS, pas en colonnes ─────────────────────────────────
 *
 * Dix-huit trous sur quatre départs font soixante-douze cases. Étalées, elles
 * demandent un défilement horizontal et des champs de trois millimètres :
 * impossible à saisir, illisible à relire. On choisit donc un départ, on
 * remplit ses dix-huit distances, on passe au suivant.
 *
 * Le par et l'index, eux, sont COMMUNS à tous les départs — c'est le même trou
 * — et restent donc affichés quel que soit l'onglet.
 *
 * ── Pourquoi les distances sont séparées des trous ──────────────────────────
 *
 * Un même trou fait 380 m des blancs et 310 m des rouges. Les écraser en une
 * seule valeur rendrait le carnet faux pour la moitié des joueurs, et c'est
 * pourquoi la base les range à part.
 */

/** Un champ numérique de carnet.
 *
 *  Chaîne propre plutôt que nombre lié : un champ lié à un nombre ne peut
 *  jamais être vide, et le zéro résiduel se colle devant la frappe suivante —
 *  on tape 3 en voulant 350 et on lit « 03 ». Le même piège qu'à la saisie de
 *  distance d'un coup, corrigé de la même façon. */
function Champ({ value, onChange, max, placeholder, aria }: {
    value: number | null;
    onChange: (v: number | null) => void;
    max: number;
    placeholder: string;
    aria: string;
}) {
    const [draft, setDraft] = useState(value == null ? '' : String(value));
    useEffect(() => { setDraft(value == null ? '' : String(value)); }, [value]);
    return (
        <input
            type="text"
            inputMode="numeric"
            aria-label={aria}
            className="input input-bordered h-12 w-full px-1 text-center text-base tabular-nums"
            placeholder={placeholder}
            value={draft}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => {
                const brut = e.target.value.replace(/\D/g, '').slice(0, String(max).length);
                setDraft(brut);
                onChange(brut === '' ? null : Math.min(max, Number(brut)));
            }}
            onBlur={() => setDraft(value == null ? '' : String(value))}
        />
    );
}

/** Ce qu'un départ dit de la difficulté, pour les deux genres. */
interface Difficulte {
    slopeHommes: number | null;
    slopeFemmes: number | null;
    courseRatingHommes: number | null;
    courseRatingFemmes: number | null;
}

const VIDE: Difficulte = {
    slopeHommes: null, slopeFemmes: null,
    courseRatingHommes: null, courseRatingFemmes: null,
};

export function CourseCard() {
    const { id } = useParams<{ id: string }>();
    const [nom, setNom] = useState('');
    const [tees, setTees] = useState<string[]>([]);
    /** Le plafond du handicap de jeu imposé par CE parcours, s'il en impose un. */
    const [plafond, setPlafond] = useState<number | null>(null);
    /* La difficulté de chaque départ, par libellé.
     *
     * Gardée à côté de la liste des libellés plutôt que dans un objet unique :
     * le reste de l'écran manipule des libellés depuis toujours — on ajoute, on
     * retire, on renomme — et la difficulté n'est qu'une donnée de plus
     * attachée au même nom. */
    const [difficulte, setDifficulte] = useState<Record<string, Difficulte>>({});
    const [holes, setHoles] = useState<CardHole[]>([]);
    const [actif, setActif] = useState<string | null>(null);
    const [canEdit, setCanEdit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [nouveauTee, setNouveauTee] = useState('');

    useEffect(() => {
        if (!id) return;
        void getCourseCard(id)
            .then((c) => {
                setNom(c.course.name);
                setCanEdit(c.canEdit);
                const labels = c.tees.map((t) => t.label);
                setTees(labels);
                setDifficulte(Object.fromEntries(c.tees.map((t) => [t.label, {
                    slopeHommes: t.slopeHommes,
                    slopeFemmes: t.slopeFemmes,
                    courseRatingHommes: t.courseRatingHommes,
                    courseRatingFemmes: t.courseRatingFemmes,
                }])));
                setActif(labels[0] ?? null);
                setHoles(c.holes);
                setPlafond(c.course.maxHandicapStrokes);
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : 'Chargement impossible'))
            .finally(() => setLoading(false));
    }, [id]);

    const majTrou = (numero: number, patch: Partial<CardHole>): void => {
        setHoles((prev) => prev.map((h) => (h.number === numero ? { ...h, ...patch } : h)));
        setSaved(false);
    };

    const majDistance = (numero: number, metres: number | null): void => {
        if (!actif) return;
        setHoles((prev) => prev.map((h) => (h.number === numero
            ? { ...h, distances: { ...h.distances, [actif]: metres } }
            : h)));
        setSaved(false);
    };

    const majDifficulte = (label: string, patch: Partial<Difficulte>): void => {
        setDifficulte((prev) => ({
            ...prev,
            [label]: { ...VIDE, ...prev[label], ...patch },
        }));
    };

    const ajouterTee = (label: string): void => {
        const propre = label.trim();
        if (!propre || tees.includes(propre)) return;
        setTees((prev) => [...prev, propre]);
        setActif(propre);
        setNouveauTee('');
        setSaved(false);
    };

    const retirerTee = (label: string): void => {
        setTees((prev) => prev.filter((t) => t !== label));
        setActif((a) => (a === label ? tees.find((t) => t !== label) ?? null : a));
        setSaved(false);
    };

    const enregistrer = async (): Promise<void> => {
        if (!id) return;
        setBusy(true); setError(null);
        try {
            await saveCourseCard(id, {
                maxHandicapStrokes: plafond,
                tees: tees.map((label) => ({ label, ...(difficulte[label] ?? VIDE) })),
                holes: holes.map((h) => ({
                    number: h.number,
                    par: h.par,
                    strokeIndex: h.strokeIndex,
                    distances: h.distances,
                })),
            });
            setSaved(true);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        } finally { setBusy(false); }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    // Le total du départ affiché : c'est le premier chiffre qu'on vérifie sur
    // un carnet, et celui qui trahit une distance saisie de travers.
    const totalDepart = actif
        ? holes.reduce((s, h) => s + (h.distances[actif] ?? 0), 0)
        : 0;
    const totalPar = holes.reduce((s, h) => s + (h.par ?? 0), 0);

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/parcours" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Parcours
                </Link>
                <h1 className="text-2xl font-bold tracking-tight">{nom}</h1>
                <p className="mt-1 text-sm opacity-70">
                    Carnet de parcours · {holes.length} trous
                    {totalPar > 0 && ` · par ${totalPar}`}
                    {actif && totalDepart > 0 && ` · ${totalDepart} m depuis ${actif}`}
                </p>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {!canEdit && (
                <p className="rounded-box bg-base-200 p-3 text-sm opacity-80">
                    Carnet en lecture seule : seul son auteur ou un administrateur peut le modifier.
                </p>
            )}

            {/* Les départs. Le premier de la liste est le plus long, comme sur
                le carnet papier — c'est l'ordre que l'œil cherche. */}
            <section className="rounded-box bg-base-200 p-4">
                <h2 className="text-sm font-semibold">Départs</h2>
                {tees.length === 0 ? (
                    <p className="mt-1 text-xs opacity-70">
                        Aucun départ. Ajoutez-en un pour saisir des distances.
                    </p>
                ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {tees.map((t) => (
                            <span key={t} className="flex items-center">
                                <button type="button" onClick={() => setActif(t)}
                                    aria-pressed={actif === t}
                                    className={`h-12 rounded-field px-4 text-base font-bold transition-colors ${
                                        actif === t ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                                    }`}>
                                    {t}
                                </button>
                                {canEdit && (
                                    <button type="button" onClick={() => retirerTee(t)}
                                        aria-label={`Retirer le départ ${t}`}
                                        className="btn btn-ghost btn-sm btn-square text-error">
                                        <X size={16} />
                                    </button>
                                )}
                            </span>
                        ))}
                    </div>
                )}

                {canEdit && (
                    <div className="mt-3">
                        <div className="flex flex-wrap gap-2">
                            {DEFAULT_TEES.filter((t) => !tees.includes(t)).map((t) => (
                                <button key={t} type="button" onClick={() => ajouterTee(t)}
                                    className="btn btn-outline btn-sm gap-1">
                                    <Plus size={14} /> {t}
                                </button>
                            ))}
                        </div>
                        <div className="mt-2 flex gap-2">
                            <input type="text" maxLength={40}
                                className="input input-bordered h-12 flex-1"
                                placeholder="Autre départ"
                                value={nouveauTee}
                                onChange={(e) => setNouveauTee(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') ajouterTee(nouveauTee); }} />
                            <button type="button" onClick={() => ajouterTee(nouveauTee)}
                                className="btn btn-neutral h-12">Ajouter</button>
                        </div>
                    </div>
                )}
            </section>

            {/* Le plafond du handicap, quand CE parcours en impose un.
                Les conventions ne sont pas les mêmes partout : un club limite à
                36 quand un autre laisse aller jusqu'à 54, et le joueur qui
                fréquente les deux ne peut pas avoir raison avec son seul
                réglage. Vide, c'est celui du joueur qui vaut. */}
            {canEdit && (
                <section className="rounded-box bg-base-200 p-3">
                    <label className="block">
                        <span className="px-1 text-xs font-semibold uppercase tracking-wide opacity-70">
                            Coups reçus maximum sur ce parcours
                        </span>
                        <input type="number" inputMode="numeric" min={1} max={54}
                            className="input input-bordered mt-2 h-12 w-full text-center text-base tabular-nums"
                            placeholder="réglage du joueur"
                            value={plafond ?? ''}
                            onChange={(e) => setPlafond(
                                e.target.value === '' ? null : Number(e.target.value),
                            )} />
                    </label>
                    <p className="mt-2 px-1 text-xs opacity-60">
                        À remplir seulement si ce club impose sa propre limite. Elle
                        l’emporte alors sur le réglage de chaque joueur. Laissé vide,
                        c’est celui du joueur qui vaut.
                    </p>
                </section>
            )}

            {/* Ce que le départ choisi dit de la DIFFICULTÉ.
                Deux nombres qui ne servent qu'à une chose, mais une chose
                importante : les coups reçus d'un joueur se calculent avec eux.
                Sans eux le calcul retombe sur le cas neutre — l'index tel quel —
                ce que la légende annonce plutôt que de le laisser deviner. */}
            {canEdit && actif && (
                <section className="rounded-box bg-base-200 p-3">
                    <p className="px-1 text-xs font-semibold uppercase tracking-wide opacity-70">
                        Difficulté du départ {actif}
                    </p>
                    {/* DEUX colonnes, messieurs et dames : c'est ainsi qu'une
                        carte française publie ces valeurs, et deux partenaires
                        sur le même repère ne reçoivent pas le même nombre de
                        coups. Les recopier dans une seule colonne rendrait l'un
                        des deux faux. */}
                    {([
                        ['Messieurs', 'slopeHommes', 'courseRatingHommes'],
                        ['Dames', 'slopeFemmes', 'courseRatingFemmes'],
                    ] as const).map(([mot, cleSlope, cleSSS]) => (
                        <div key={mot} className="mt-2">
                            <p className="px-1 text-xs font-semibold opacity-70">{mot}</p>
                            <div className="mt-1 grid grid-cols-2 gap-2">
                                <label className="flex flex-col gap-1">
                                    <span className="text-xs opacity-60">Slope</span>
                                    <input type="number" inputMode="numeric" min={55} max={155}
                                        className="input input-bordered h-12 text-center text-base tabular-nums"
                                        placeholder="113"
                                        value={difficulte[actif]?.[cleSlope] ?? ''}
                                        onChange={(e) => majDifficulte(actif, {
                                            [cleSlope]: e.target.value === '' ? null : Number(e.target.value),
                                        })} />
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span className="text-xs opacity-60">SSS</span>
                                    <input type="number" inputMode="decimal" min={25} max={90} step="0.1"
                                        className="input input-bordered h-12 text-center text-base tabular-nums"
                                        placeholder="—"
                                        value={difficulte[actif]?.[cleSSS] ?? ''}
                                        onChange={(e) => majDifficulte(actif, {
                                            [cleSSS]: e.target.value === '' ? null : Number(e.target.value),
                                        })} />
                                </label>
                            </div>
                        </div>
                    ))}
                    <p className="mt-2 px-1 text-xs opacity-60">
                        Ils décident des coups reçus, et chaque joueur prend ceux de son
                        genre. Laissés vides, le calcul prend l’index tel quel.
                    </p>
                </section>
            )}

            {/* Le tableau. Une ligne par trou, trois colonnes : par, index, et
                la distance du départ choisi. */}
            <section className="rounded-box bg-base-200 p-3">
                <div className="grid grid-cols-[2.2rem_1fr_1fr_1fr] items-center gap-2 px-1 pb-2 text-xs font-semibold uppercase tracking-wide opacity-70">
                    <span>Trou</span>
                    <span className="text-center">Par</span>
                    <span className="text-center">Index</span>
                    <span className="text-center">{actif ? `${actif} (m)` : 'Distance'}</span>
                </div>
                <ol className="space-y-1.5">
                    {holes.map((h) => (
                        <li key={h.number}
                            className="grid grid-cols-[2.2rem_1fr_1fr_1fr] items-center gap-2">
                            <span className="text-center text-base font-bold tabular-nums">{h.number}</span>
                            <Champ value={h.par} max={6} placeholder="4"
                                aria={`Par du trou ${h.number}`}
                                onChange={(v) => majTrou(h.number, { par: v })} />
                            <Champ value={h.strokeIndex} max={18} placeholder="—"
                                aria={`Index du trou ${h.number}`}
                                onChange={(v) => majTrou(h.number, { strokeIndex: v })} />
                            {actif ? (
                                <Champ value={h.distances[actif] ?? null} max={900} placeholder="—"
                                    aria={`Distance du trou ${h.number} depuis ${actif}`}
                                    onChange={(v) => majDistance(h.number, v)} />
                            ) : (
                                <span className="text-center text-xs opacity-50">—</span>
                            )}
                        </li>
                    ))}
                </ol>
            </section>

            {canEdit && (
                <div className="space-y-2">
                    <button type="button" disabled={busy} onClick={() => void enregistrer()}
                        className="btn btn-primary btn-block h-14 text-lg font-bold tap-target">
                        Enregistrer le carnet
                    </button>
                    {/* Un accusé explicite : sans lui on ne sait pas si les
                        soixante-douze cases sont parties, et on réenregistre
                        par précaution. */}
                    {saved && (
                        <p className="text-center text-sm text-success">Carnet enregistré.</p>
                    )}
                </div>
            )}
        </div>
    );
}
